import { type ReactNode, useEffect, useState } from 'react';
import type React from 'react';
import { Icon } from '@timeedit/ui-components';
import {
  Button,
  Input,
  Modal,
  Radio,
  Select,
  Tooltip,
} from '@timeedit/ui-components/antd';
import intl from '../../i18n/intl';
import { ListSearch } from '../ListSearch';
import './index.scss';

type Props = {
  sectionHeading?: string;
  listTitle?: string;
  number: number;
  children?: React.ReactNode | null;
  onSelect?: any;
  onFilterAndSortChange?: any;
  extraWidth?: boolean;
  extra?: ReactNode;
  plusButtonEl?: (onSelect: Function) => ReactNode;
  filterData?: any[];
  sortingData?: any[];
  listData?: any[];
  searchProp?: string;
  searchMenu?: any;
};

const language: any = intl.messages;

const ListViewHOC = ({
  sectionHeading,
  listTitle,
  number,
  children,
  onSelect,
  onFilterAndSortChange = (list: any[]) => {},
  extraWidth,
  extra = undefined,
  plusButtonEl = undefined,
  filterData = [],
  sortingData = [],
  listData = [],
  searchProp = '',
  searchMenu,
}: Props) => {
  const classNames = [];
  if (onSelect === 'hidden') {
    classNames.push('not-visible');
  }
  const classNamesWrapper = ['list--wrapper'];
  if (extraWidth) {
    classNamesWrapper.push('extra-width--wrapper');
  } else {
    classNamesWrapper.push('normal-width--wrapper');
  }

  const [data, setData] = useState(listData);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [isSortingModalVisible, setIsSortingModalVisible] = useState(false);

  useEffect(() => {
    if (listData?.length) applySortingAndFilters(changeSortingAndFilters());
  }, [listData]);

  const onSortingChange = (optionIndex: any, order: string) => {
    sortingData = sortingData.map((sortingOption: any, index: number) => {
      if (index === optionIndex) {
        return { ...sortingOption, active: true, order };
      }
      return { ...sortingOption, active: false };
    });
    changeSortingAndFilters();
  };

  const changeSortingAndFilters = () => {
    let data = listData;
    for (const sortingOption of sortingData) {
      if (sortingOption.active)
        data = data.sort((a: any, b: any) => {
          switch (sortingOption.type) {
            case 'text':
              if (sortingOption.order === 'ASC')
                return b.listLabel.localeCompare(a.listLabel);
              else return a.listLabel.localeCompare(b.listLabel);
            default:
              if (sortingOption.order === 'ASC')
                return a[sortingOption.property] - b[sortingOption.property];
              else return b[sortingOption.property] - a[sortingOption.property];
          }
        });
    }

    for (const filterOption of filterData) {
      // Find filters that have a set value and apply them
      if (filterOption.value || filterOption.type === 'boolean') {
        switch (filterOption.type) {
          case 'text':
            data = data.filter((element: any) =>
              element[filterOption.property]
                .toLowerCase()
                .includes(filterOption.value.toLowerCase())
            );
            break;
          case 'boolean':
            if (filterOption.value === 'all') continue;
            else
              data = data.filter(
                (element: any) =>
                  element[filterOption.property] === filterOption.value
              );
            break;
          case 'select':
            if (filterOption.dataType === 'array') {
              if (filterOption.value.length > 0) {
                if (filterOption.mode === 'existsOn') {
                  const optionProperties = filterOption.options.filter(
                    (option: any) =>
                      filterOption.value.includes(option.id.toString())
                  );
                  data = data.filter((element: any) =>
                    optionProperties.some((item: any) =>
                      item[filterOption.matchingProperty].includes(
                        element[filterOption.property]
                      )
                    )
                  );
                } else
                  data = data.filter((element: any) =>
                    filterOption.value.every((item: any) =>
                      element[filterOption.property].includes(item)
                    )
                  );
              }
            } else
              data = data.filter(
                (element: any) =>
                  element[filterOption.property] === filterOption.value
              );
            break;
          default:
            data = data.filter(
              (element: any) =>
                element[filterOption.property] === filterOption.value
            );
        }
      }
    }
    setData(data);
    return data;
  };

  const onSearch = (value: string | null = null) => {
    if (searchProp) {
      if (value)
        applySortingAndFilters(
          data.filter((user: any) =>
            user[searchProp].toLowerCase().includes(value.toLowerCase())
          )
        );
      else applySortingAndFilters(changeSortingAndFilters());
    }
  };

  const applySortingAndFilters = (list: any[] | false = false) =>
    onFilterAndSortChange(list || data, filterData, sortingData);

  const showFilterModal = () => setIsFilterModalVisible(!isFilterModalVisible);
  const showSortingModal = () =>
    setIsSortingModalVisible(!isSortingModalVisible);

  return (
    <div className={classNamesWrapper.join(' ')}>
      <div
        style={{
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="top-heading--list">
          {plusButtonEl ? (
            plusButtonEl(onSelect)
          ) : onSelect ? (
            <Tooltip title={language.add}>
              <Button
                size="small"
                type="default"
                onClick={onSelect}
                icon={<Icon id="PlusOutlined" alt="Plus" />}
                className={classNames.join(' ')}
              />
            </Tooltip>
          ) : (
            <></>
          )}
          <div className="list-counter">{number}</div>
        </div>
        <div className="top-heading--list">
          {!!filterData?.length && (
            <Tooltip title={language.filter}>
              <Button
                size="small"
                type="default"
                icon={<Icon id="FilterOutlined" alt="Filter" />}
                className={classNames.join(' ')}
                onClick={() => showFilterModal()}
              />
            </Tooltip>
          )}
          {!!sortingData?.length && (
            <Tooltip title={language.sort}>
              <Button
                size="small"
                type="default"
                icon={
                  <Icon id="SortDescendingOutlined" alt="Sort Descending" />
                }
                className={classNames.join(' ')}
                onClick={() => showSortingModal()}
              />
            </Tooltip>
          )}
        </div>
        {!!extra && <div className="extra--wrapper">{extra}</div>}
      </div>
      {searchProp && <ListSearch onSearch={onSearch} menu={searchMenu} />}
      {children}
      <Modal
        open={isFilterModalVisible}
        onCancel={showFilterModal}
        onOk={() => {
          applySortingAndFilters();
          showFilterModal();
        }}
      >
        <span style={{ fontWeight: 'bold' }}>Filter options</span>
        {filterData.map((filterOption: any, index: number) => {
          switch (filterOption.type) {
            case 'select':
              return (
                <div className={'filter-option'}>
                  <span className={'popover-span'}>{filterOption.label}</span>
                  <Select
                    allowClear
                    showSearch
                    size="small"
                    style={{ width: '70%' }}
                    value={filterData[index].value}
                    onChange={(value) => {
                      filterData[index].value = value;
                      changeSortingAndFilters();
                    }}
                    mode={
                      filterOption.dataType === 'array' ? 'multiple' : undefined
                    }
                  >
                    {filterOption.options.map((value: any) => {
                      return (
                        <Select.Option key={value.id}>
                          {value.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </div>
              );
            case 'text':
              return (
                <div className={'filter-option'}>
                  <span className={'popover-span'}>{filterOption.label}</span>
                  <Input
                    size="small"
                    value={filterData[index].value}
                    onChange={(event) => {
                      filterData[index].value = event.target.value;
                      changeSortingAndFilters();
                    }}
                  />
                </div>
              );
            case 'boolean':
              return (
                <div className={'filter-option'}>
                  <span className={'popover-span'}>{filterOption.label}</span>
                  <Radio.Group
                    value={filterData[index].value}
                    onChange={(event) => {
                      filterData[index].value = event.target.value;
                      changeSortingAndFilters();
                    }}
                  >
                    {filterOption.options.map((option: any) => (
                      <Radio.Button key={option.value} value={option.value}>
                        {option.name}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              );
            default:
              return '';
          }
        })}
      </Modal>
      <Modal
        open={isSortingModalVisible}
        onCancel={showSortingModal}
        onOk={() => {
          applySortingAndFilters();
          showSortingModal();
        }}
      >
        <span style={{ fontWeight: 'bold' }}>Sorting options</span>
        {sortingData.map((sortingOption: any, index: number) => {
          return (
            <div className={'sorting-option'} key={index}>
              <span className={'popover-span'}>{sortingOption.label}</span>
              <div>
                <Tooltip title={'Ascending'}>
                  <Button
                    size="small"
                    type="default"
                    icon={
                      <Icon id="SortDescendingOutlined" alt="Sort Descending" />
                    }
                    onClick={() => {
                      onSortingChange(index, 'ASC');
                    }}
                  />
                </Tooltip>
                <Tooltip title={'Descending'}>
                  <Button
                    size="small"
                    type="default"
                    icon={
                      <Icon id="SortAscendingOutlined" alt="Sort Ascending" />
                    }
                    onClick={() => {
                      onSortingChange(index, 'DESC');
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          );
        })}
      </Modal>
    </div>
  );
};

export default ListViewHOC;
